/**
 * Phase 8 support agent console (SUPPORT.md). One thread truth for assistant, agent and ticket. The
 * agent sees the full transcript (assistant turns with confidence), internal notes (never shown to the
 * user), user context limited to identifiers/states (PII stays behind the audited reveal in Customer
 * 360), and the linked request. Every change carries the thread's expected version.
 */
import {
  AppContainer,
  Badge,
  Button,
  Card,
  cx,
  DataTable,
  FilterChips,
  formatRelative,
  Ltr,
  PageHeader,
  SearchInput,
  StateView,
  useToast,
} from '@hopex/ui';
import { Link } from '@tanstack/react-router';
import { Bot, Headset, Lock, ShieldAlert, UserRound } from 'lucide-react';
import { AgentAttachButton, AgentAttachment, type Attachment } from './Attachments.tsx';
import { useEffect, useState } from 'react';
import { api, ApiFailure, dt, errorMessage } from '../../api.ts';
import { Remote, useRemote } from '../../lib/remote.tsx';

export const SUPPORT_STATUS: Record<
  string,
  { label: string; tone: 'neutral' | 'accent' | 'positive' | 'negative' }
> = {
  CHAT: { label: 'گفتگو با دستیار', tone: 'neutral' },
  OPEN: { label: 'باز', tone: 'accent' },
  WAITING_AGENT: { label: 'منتظر کارشناس', tone: 'negative' },
  ESCALATED: { label: 'ارجاع‌شده', tone: 'negative' },
  WAITING_USER: { label: 'منتظر کاربر', tone: 'accent' },
  RESOLVED: { label: 'حل شد', tone: 'positive' },
  CLOSED: { label: 'بسته', tone: 'neutral' },
};
export const SUPPORT_CATEGORY: Record<string, string> = {
  ACCOUNT: 'حساب کاربری',
  KYC: 'احراز هویت',
  TOMAN_DEPOSIT: 'واریز تومان',
  TOMAN_WITHDRAWAL: 'برداشت تومان',
  CRYPTO_DEPOSIT: 'واریز رمزارز',
  CRYPTO_WITHDRAWAL: 'برداشت رمزارز',
  CONVERT: 'خرید / فروش / تبدیل',
  BANK_ACCOUNTS: 'حساب‌های بانکی',
  FEES_RATES: 'کارمزد و قیمت',
  SECURITY: 'امنیت',
  NETWORK_TXID: 'شبکه و TXID',
  NOTIFICATIONS: 'اعلان‌ها',
  PRIVACY: 'حریم خصوصی',
  OTHER: 'سایر',
};
const PRIORITY: Record<string, string> = {
  LOW: 'کم',
  NORMAL: 'عادی',
  HIGH: 'مهم',
  URGENT: 'فوری',
};
/** Starting points for common answers — the agent always reads and edits before sending. */
const CANNED: { label: string; text: string }[] = [
  {
    label: 'سلام و پیگیری',
    text: 'سلام، وقت شما بخیر. درخواست شما دریافت شد و در حال بررسی است. نتیجه را در همین تیکت اطلاع می‌دهیم.',
  },
  {
    label: 'نیاز به کد پیگیری',
    text: 'برای بررسی دقیق، لطفاً کد پیگیری تراکنش (یا TXID) و زمان تقریبی انجام آن را در همین تیکت بفرستید.',
  },
  {
    label: 'نیاز به رسید',
    text: 'لطفاً تصویر رسید بانکی یا صفحه‌ی تراکنش را از دکمه‌ی «پیوست فایل» در همین تیکت ارسال کنید.',
  },
  {
    label: 'واریز ثبت شد',
    text: 'واریز شما بررسی و به کیف پولتان اضافه شد. اگر سؤال دیگری دارید در همین تیکت بنویسید.',
  },
  {
    label: 'هشدار امنیتی',
    text: 'یادآوری مهم: پشتیبانی هوپکس هرگز رمز عبور، کد پیامکی یا عبارت بازیابی کیف پول از شما نمی‌خواهد. این اطلاعات را برای هیچ‌کس نفرستید.',
  },
  {
    label: 'حل شد',
    text: 'خوشحالیم که مشکل برطرف شد. اگر سؤال دیگری داشتید، در همین تیکت یا یک تیکت جدید با ما در ارتباط باشید.',
  },
];

const LINK_FA: Record<string, string> = {
  TOMAN_DEPOSIT: 'واریز تومان',
  TOMAN_WITHDRAWAL: 'برداشت تومان',
  CRYPTO_DEPOSIT: 'واریز رمزارز',
  CRYPTO_WITHDRAWAL: 'برداشت رمزارز',
  CONVERSION: 'سفارش تبدیل',
};

export interface SupportRow {
  id: string;
  ref: string;
  subject: string;
  category: string;
  status: string;
  priority: string;
  tier: string;
  linked: string | null;
  assignee: string | null;
  mine: boolean;
  /** Null for a public-site guest conversation (OD-65). */
  userId: string | null;
  userPublicId: string | null;
  origin?: string;
  guestPhone?: string | null;
  lastUserMessageAt: string | null;
  updatedAt: string;
}

const VIEWS = [
  { id: 'OPEN_WORK', label: 'کارهای باز' },
  { id: 'UNASSIGNED', label: 'بدون مسئول' },
  { id: 'MINE', label: 'پرونده‌های من' },
  { id: 'ALL', label: 'همه' },
];

export function StatusChip({ status }: { status: string }) {
  const s = SUPPORT_STATUS[status] ?? { label: status, tone: 'neutral' as const };
  return <Badge tone={s.tone}>{s.label}</Badge>;
}

export function SupportThreadsTable({ rows, label }: { rows: SupportRow[]; label: string }) {
  return (
    <DataTable<SupportRow>
      label={label}
      rows={rows}
      columns={[
        {
          id: 'subject',
          header: 'موضوع',
          isRowHeader: true,
          cell: (r) => (
            <Link
              to="/support/$id"
              params={{ id: r.id }}
              className="font-semibold hover:underline"
              data-support-row={r.ref}
            >
              {r.subject}
              <span className="block text-[11.5px] font-normal text-fg-subtle">
                {SUPPORT_CATEGORY[r.category] ?? r.category} · <Ltr>{r.ref}</Ltr>
              </span>
            </Link>
          ),
        },
        { id: 'status', header: 'وضعیت', cell: (r) => <StatusChip status={r.status} /> },
        {
          id: 'priority',
          header: 'اولویت',
          cell: (r) => (
            <Badge
              tone={
                r.priority === 'URGENT' ? 'negative' : r.priority === 'HIGH' ? 'accent' : 'neutral'
              }
            >
              {PRIORITY[r.priority] ?? r.priority}
            </Badge>
          ),
        },
        {
          // Owner 2026-10-15: how long the customer has been waiting for HopEx (help-desk SLA view).
          id: 'wait',
          header: 'در انتظار پاسخ',
          cell: (r) => {
            const waiting = ['OPEN', 'WAITING_AGENT', 'ESCALATED'].includes(r.status);
            if (!waiting || !r.lastUserMessageAt) return '—';
            const hours = (Date.now() - new Date(r.lastUserMessageAt).getTime()) / 3_600_000;
            return (
              <span
                className={
                  hours >= 24
                    ? 'font-bold text-negative'
                    : hours >= 4
                      ? 'font-semibold text-pending'
                      : 'text-fg-muted'
                }
                data-wait-hours={Math.floor(hours)}
              >
                {formatRelative(r.lastUserMessageAt)}
              </span>
            );
          },
        },
        {
          id: 'user',
          header: 'کاربر',
          cell: (r) =>
            r.userId ? (
              <Link to="/users/$id" params={{ id: r.userId }} className="hover:underline">
                <Ltr>{r.userPublicId}</Ltr>
              </Link>
            ) : (
              <span className="text-[12px] text-fg-muted" data-support-guest>
                مهمان سایت · <Ltr>{r.guestPhone ?? '—'}</Ltr>
              </span>
            ),
        },
        {
          id: 'assignee',
          header: 'مسئول',
          cell: (r) => (r.assignee ? <Ltr>{r.assignee}</Ltr> : '—'),
        },
        {
          id: 'linked',
          header: 'درخواست مرتبط',
          cell: (r) => (r.linked ? LINK_FA[r.linked] : '—'),
        },
        { id: 'updated', header: 'آخرین تغییر', cell: (r) => formatRelative(r.updatedAt) },
      ]}
      empty={
        <p className="m-0 text-center text-[13px] text-fg-muted">پرونده‌ای در این نما نیست.</p>
      }
    />
  );
}

export function SupportInboxPage() {
  const [view, setView] = useState('OPEN_WORK');
  const [category, setCategory] = useState('');
  const [priority, setPriority] = useState('');
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const params = new URLSearchParams({ view });
  if (category) params.set('category', category);
  if (priority) params.set('priority', priority);
  if (query) params.set('q', query);
  const r = useRemote<{ items: SupportRow[] }>(`/support?${params.toString()}`);
  // The queue keeps itself current (new chats / replies) while the tab is visible.
  const { reload } = r;
  useEffect(() => {
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') reload();
    }, 15_000);
    return () => window.clearInterval(t);
  }, [reload]);
  return (
    <AppContainer>
      <PageHeader
        title="پشتیبانی و گفتگو"
        description="صندوق پرونده‌های پشتیبانی: گفتگوی دستیار، پاسخ کارشناس و تیکت در یک رشته."
      />
      <div className="mb-4 flex flex-col gap-3" data-support-inbox>
        <FilterChips label="نمای صف" options={VIEWS} value={view} onChange={setView} />
        <div className="flex flex-wrap items-end gap-3">
          <form
            className="w-full max-w-xs"
            onSubmit={(e) => {
              e.preventDefault();
              setQuery(q.trim());
            }}
          >
            <SearchInput
              label="جستجوی دقیق"
              placeholder="HX-T-… یا HX-…"
              value={q}
              onChange={(v) => {
                setQ(v);
                if (!v) setQuery('');
              }}
            />
          </form>
          <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-fg-muted">
            موضوع
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="h-10 rounded-md border border-line-strong bg-surface-1 px-3 text-[13px] font-normal text-fg"
            >
              <option value="">همه</option>
              {Object.entries(SUPPORT_CATEGORY).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-fg-muted">
            اولویت
            <select
              value={priority}
              onChange={(e) => setPriority(e.target.value)}
              className="h-10 rounded-md border border-line-strong bg-surface-1 px-3 text-[13px] font-normal text-fg"
              data-support-priority-filter
            >
              <option value="">همه</option>
              {Object.entries(PRIORITY).map(([k, v]) => (
                <option key={k} value={k}>
                  {v}
                </option>
              ))}
            </select>
          </label>
        </div>
      </div>
      <Remote r={r}>
        {(d) => {
          const waiting = d.items.filter((x) =>
            ['OPEN', 'WAITING_AGENT', 'ESCALATED'].includes(x.status),
          );
          const late = waiting.filter(
            (x) =>
              x.lastUserMessageAt &&
              Date.now() - new Date(x.lastUserMessageAt).getTime() >= 24 * 3_600_000,
          ).length;
          return (
            <>
              {/* At a glance: what needs an agent now. */}
              <div className="mb-3 grid grid-cols-2 gap-2 sm:grid-cols-4" data-support-counters>
                {(
                  [
                    ['منتظر پاسخ کارشناس', waiting.length, ''],
                    [
                      'فوری',
                      waiting.filter((x) => x.priority === 'URGENT').length,
                      'text-negative',
                    ],
                    ['بیش از ۲۴ ساعت', late, late ? 'text-negative' : ''],
                    [
                      'منتظر پاسخ کاربر',
                      d.items.filter((x) => x.status === 'WAITING_USER').length,
                      '',
                    ],
                  ] as const
                ).map(([label, n, tone]) => (
                  <Card key={label} className="flex flex-col gap-0.5 p-3">
                    <span className="text-[12px] text-fg-muted">{label}</span>
                    <span className={`hx-num text-[20px] font-black ${tone}`}>
                      {n.toLocaleString('fa-IR')}
                    </span>
                  </Card>
                ))}
              </div>
              <Card className="p-3 sm:p-4">
                <SupportThreadsTable rows={d.items} label="پرونده‌های پشتیبانی" />
              </Card>
            </>
          );
        }}
      </Remote>
    </AppContainer>
  );
}

interface ThreadDetail {
  id: string;
  ref: string;
  subject: string;
  category: string;
  status: string;
  priority: string;
  tier: string;
  channelOrigin: string;
  version: string;
  assignee: string | null;
  mine: boolean;
  reopenUntil: string | null;
  createdAt: string;
  firstRespondedAt: string | null;
  /** Null for a public-site guest (OD-65); then `guest` is set. */
  user: { id: string; publicId: string; status: string; kyc1: string; since: string } | null;
  guest?: { phoneMasked: string; matchedUserId: string | null; verified: false } | null;
  linked: { type: string; id: string; ref: string | null } | null;
  messages: {
    id: string;
    author: 'USER' | 'AI' | 'AGENT' | 'SYSTEM';
    agent: string | null;
    body: string;
    internal: boolean;
    redacted: boolean;
    articles: string[];
    ai: { confidence: string; escalated: boolean; model: string } | null;
    at: string;
  }[];
  events: { type: string; actor: string; at: string }[];
  attachments?: Attachment[];
}

const AUTHOR = {
  USER: { label: 'کاربر', icon: UserRound },
  AI: { label: 'دستیار', icon: Bot },
  AGENT: { label: 'کارشناس', icon: Headset },
  SYSTEM: { label: 'سیستم', icon: Headset },
};
const USER_STATUS_FA: Record<string, string> = {
  ACTIVE: 'فعال',
  SUSPENDED: 'تعلیق',
  LOCKED: 'قفل',
  CLOSED: 'بسته',
};
const KYC1_FA: Record<string, string> = {
  VERIFIED: 'تأییدشده',
  NOT_STARTED: 'انجام نشده',
  CHECKING: 'در حال بررسی',
  NEEDS_CORRECTION: 'نیازمند اصلاح',
  REJECTED: 'رد شده',
  PROVIDER_UNAVAILABLE: 'سرویس استعلام در دسترس نبود',
};
const EVENT_FA: Record<string, string> = {
  THREAD_STARTED: 'شروع گفتگو',
  TICKET_CREATED: 'ثبت تیکت',
  ESCALATED: 'ارجاع به کارشناس',
  ASSISTANT_SOLVED: 'حل‌شده با پاسخ دستیار',
  REOPENED: 'بازگشایی توسط کاربر',
  AGENT_REPLIED: 'پاسخ کارشناس',
  NOTE_ADDED: 'یادداشت داخلی',
  AGENT_UPDATED: 'تغییر توسط کارشناس',
};
const ACTOR_FA: Record<string, string> = {
  USER: 'کاربر',
  AI: 'دستیار',
  AGENT: 'کارشناس',
  SYSTEM: 'سیستم',
};
const CONF: Record<string, string> = { HIGH: 'اطمینان بالا', LOW: 'اطمینان کم', NONE: 'بدون پاسخ' };
const LINK_PATH: Record<string, string> = {
  TOMAN_DEPOSIT: '/toman-deposits',
  TOMAN_WITHDRAWAL: '/toman-withdrawals',
  CRYPTO_DEPOSIT: '/crypto-deposits',
  CRYPTO_WITHDRAWAL: '/crypto-withdrawals',
  CONVERSION: '/conversions',
};

export function SupportThreadPage({ id }: { id: string }) {
  const r = useRemote<ThreadDetail>(`/support/${id}`);
  const [body, setBody] = useState('');
  const [internal, setInternal] = useState(false);
  const [busy, setBusy] = useState(false);
  const toast = useToast();
  // Live chat (owner 2026-10-15): the customer's new messages appear without a refresh. The draft is
  // separate state, so a refresh never touches what the agent is typing.
  const { reload } = r;
  useEffect(() => {
    const t = window.setInterval(() => {
      if (document.visibilityState === 'visible') reload();
    }, 4_000);
    return () => window.clearInterval(t);
  }, [reload]);

  const act = async (fn: (v: string) => Promise<unknown>) => {
    if (!r.data) return;
    setBusy(true);
    try {
      await fn(r.data.version);
      r.reload();
    } catch (e) {
      toast.show({
        tone: 'danger',
        title:
          e instanceof ApiFailure && e.code === 'STALE_ACTION'
            ? 'این پرونده هم‌زمان تغییر کرده است؛ صفحه به‌روز شد.'
            : errorMessage(e),
      });
      r.reload();
    } finally {
      setBusy(false);
    }
  };
  const reply = (status?: string) =>
    act(async (v) => {
      await api.post(`/support/${id}/reply`, {
        body,
        internal,
        expectedVersion: v,
        ...(status && !internal ? { status } : {}),
      });
      setBody('');
    });
  const update = (patch: Record<string, string>) =>
    act((v) => api.post(`/support/${id}/update`, { ...patch, expectedVersion: v }));

  return (
    <AppContainer>
      <Link
        to="/support"
        className="mb-3 inline-flex text-[13px] font-semibold text-fg-muted hover:text-fg"
      >
        صندوق پشتیبانی
      </Link>
      <Remote r={r}>
        {(t) => (
          <div className="grid gap-4 xl:grid-cols-[1fr_320px]" data-agent-thread={t.status}>
            <div className="flex min-w-0 flex-col gap-4">
              <Card className="flex flex-wrap items-start justify-between gap-3 p-4">
                <div className="min-w-0">
                  <h1 className="m-0 text-[18px] font-extrabold">{t.subject}</h1>
                  <p className="m-0 text-[12.5px] text-fg-muted">
                    <Ltr>{t.ref}</Ltr> · {SUPPORT_CATEGORY[t.category] ?? t.category} · ایجاد{' '}
                    {dt(t.createdAt)}
                  </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <StatusChip status={t.status} />
                  <Badge>{PRIORITY[t.priority] ?? t.priority}</Badge>
                </div>
              </Card>
              <Card className="p-3 sm:p-4">
                <ol className="m-0 flex list-none flex-col gap-3 p-0" aria-label="پیام‌های پرونده">
                  {t.messages.map((m) => {
                    const a = AUTHOR[m.author];
                    const Icon = a.icon;
                    return (
                      <li
                        key={m.id}
                        data-agent-message={m.internal ? 'INTERNAL' : m.author}
                        className={cx(
                          'flex flex-col gap-1 rounded-md border px-3.5 py-2.5',
                          m.internal
                            ? 'border-dashed border-pending/60 bg-surface-2'
                            : m.author === 'USER'
                              ? 'border-line bg-surface-1'
                              : m.author === 'SYSTEM'
                                ? 'border-dashed border-line bg-surface-1 text-[12.5px]'
                                : 'border-accent-soft bg-accent-tint',
                        )}
                      >
                        <span className="flex flex-wrap items-center gap-1.5 text-[11.5px] font-semibold text-fg-muted">
                          <Icon aria-hidden className="size-3.5" />
                          {a.label}
                          {m.agent && <Ltr>{m.agent}</Ltr>}
                          {m.internal && (
                            <Badge tone="negative">
                              <Lock aria-hidden className="me-1 size-3" />
                              یادداشت داخلی — برای کاربر نمایش داده نمی‌شود
                            </Badge>
                          )}
                          {m.ai && (
                            <Badge>
                              {CONF[m.ai.confidence] ?? m.ai.confidence}
                              {m.ai.escalated ? ' · ارجاع به کارشناس' : ''}
                            </Badge>
                          )}
                          <span className="font-normal text-fg-subtle">· {dt(m.at)}</span>
                        </span>
                        {(() => {
                          const file = t.attachments?.find((x) => x.messageId === m.id);
                          return file ? (
                            <AgentAttachment a={file} />
                          ) : (
                            <p className="m-0 whitespace-pre-line text-[13.5px] leading-7 text-fg">
                              {m.body}
                            </p>
                          );
                        })()}
                        {m.redacted && (
                          <span className="inline-flex items-center gap-1 text-[11.5px] text-pending">
                            <ShieldAlert aria-hidden className="size-3.5" />
                            بخشی از پیام کاربر (شبیه اطلاعات محرمانه) پیش از ذخیره حذف شد.
                          </span>
                        )}
                        {m.articles.length > 0 && (
                          <span className="text-[11.5px] text-fg-muted">
                            منبع: <Ltr>{m.articles.join(', ')}</Ltr>
                          </span>
                        )}
                      </li>
                    );
                  })}
                </ol>
              </Card>
              {t.status !== 'CLOSED' ? (
                <Card className="flex flex-col gap-3 p-4" data-agent-compose>
                  {/* Canned replies (owner 2026-10-15): one tap fills the box; the agent edits it. */}
                  {!internal && (
                    <div className="flex flex-wrap items-center gap-1.5" data-canned>
                      <span className="text-[12px] font-semibold text-fg-muted">پاسخ آماده:</span>
                      {CANNED.map((c) => (
                        <button
                          key={c.label}
                          type="button"
                          onClick={() =>
                            setBody((b) => (b.trim() ? `${b.trim()}\n\n${c.text}` : c.text))
                          }
                          className="min-h-8 cursor-pointer rounded-full border border-line bg-surface-2 px-2.5 text-[12px] font-semibold text-fg-muted hover:border-accent-soft hover:text-fg"
                          data-canned-reply={c.label}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                  )}
                  <label className="flex flex-col gap-1.5 text-[13px] font-semibold">
                    {internal ? 'یادداشت داخلی (فقط برای مدیران)' : 'پاسخ به کاربر'}
                    <textarea
                      value={body}
                      onChange={(e) => setBody(e.target.value)}
                      rows={4}
                      maxLength={4000}
                      className={cx(
                        'rounded-md border bg-surface-1 p-3 text-[13.5px] font-normal leading-7 outline-hidden focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-focus',
                        internal ? 'border-pending/60' : 'border-line-strong',
                      )}
                      data-agent-body
                    />
                  </label>
                  <label className="inline-flex cursor-pointer items-center gap-2 text-[13px]">
                    <input
                      type="checkbox"
                      checked={internal}
                      onChange={(e) => setInternal(e.target.checked)}
                      className="size-4 accent-[var(--hx-accent)]"
                      data-agent-internal
                    />
                    یادداشت داخلی (به کاربر نمایش داده نمی‌شود)
                  </label>
                  <p className="m-0 text-[12px] text-fg-muted">
                    کارشناس هرگز رمز، کد یکبارمصرف، Seed Phrase یا انتقال آزمایشی درخواست نمی‌کند.
                    از این صفحه هیچ عملیات مالی انجام نمی‌شود.
                  </p>
                  <div className="flex flex-wrap gap-2">
                    <Button
                      loading={busy}
                      isDisabled={body.trim().length < 2}
                      onPress={() => void reply()}
                      data-agent-send
                    >
                      {internal ? 'ثبت یادداشت' : 'ارسال پاسخ'}
                    </Button>
                    {!internal && (
                      <Button
                        variant="secondary"
                        loading={busy}
                        isDisabled={body.trim().length < 2}
                        onPress={() => void reply('RESOLVED')}
                        data-agent-send-resolve
                      >
                        ارسال و علامت «حل شد»
                      </Button>
                    )}
                    {!internal && (
                      <AgentAttachButton
                        threadId={t.id}
                        onDone={() => {
                          toast.show({ tone: 'success', title: 'فایل برای کاربر ارسال شد.' });
                          r.reload();
                        }}
                        onError={(m) => toast.show({ tone: 'danger', title: m })}
                      />
                    )}
                  </div>
                </Card>
              ) : (
                <Card className="p-4">
                  <StateView
                    compact
                    kind="empty"
                    title="این پرونده بسته است"
                    description="برای ادامه، آن را دوباره باز کنید."
                  />
                </Card>
              )}
            </div>
            <aside className="flex flex-col gap-4">
              <Card className="flex flex-col gap-2 p-4" data-agent-user>
                <h2 className="m-0 text-[14px] font-bold">کاربر</h2>
                {t.user ? (
                  <>
                    <Link
                      to="/users/$id"
                      params={{ id: t.user.id }}
                      className="font-semibold text-accent-text hover:underline"
                    >
                      <Ltr>{t.user.publicId}</Ltr> — پرونده‌ی مشتری
                    </Link>
                    <p className="m-0 text-[12.5px] text-fg-muted">
                      وضعیت حساب: {USER_STATUS_FA[t.user.status] ?? t.user.status} · احراز سطح ۱:{' '}
                      {KYC1_FA[t.user.kyc1] ?? t.user.kyc1} · عضویت از {dt(t.user.since)}
                    </p>
                    <p className="m-0 text-[11.5px] text-fg-subtle">
                      اطلاعات هویتی فقط از طریق نمایش ممیزی‌شده در پرونده‌ی مشتری.
                    </p>
                  </>
                ) : (
                  <>
                    <p className="m-0 text-[13px] font-semibold" data-agent-guest>
                      بازدیدکننده‌ی سایت · <Ltr>{t.guest?.phoneMasked ?? '—'}</Ltr> (شماره
                      تأییدنشده)
                    </p>
                    {t.guest?.matchedUserId && (
                      <Link
                        to="/users/$id"
                        params={{ id: t.guest.matchedUserId }}
                        className="text-[12.5px] font-semibold text-accent-text hover:underline"
                      >
                        حساب هم‌شماره (تأییدنشده) — پرونده‌ی مشتری
                      </Link>
                    )}
                    <p className="m-0 text-[11.5px] text-fg-subtle">
                      گفتگوهای سایت در میز پشتیبانی با پرونده‌ی کامل پاسخ داده می‌شوند.
                    </p>
                  </>
                )}
              </Card>
              {t.linked && (
                <Card className="flex flex-col gap-1 p-4" data-agent-linked>
                  <h2 className="m-0 text-[14px] font-bold">درخواست مرتبط</h2>
                  <span className="text-[13px]">{LINK_FA[t.linked.type] ?? t.linked.type}</span>
                  {t.linked.ref && (
                    <Link
                      to={LINK_PATH[t.linked.type] ?? '/'}
                      className="text-[13px] font-semibold text-accent-text hover:underline"
                    >
                      <Ltr>{t.linked.ref}</Ltr>
                    </Link>
                  )}
                </Card>
              )}
              <Card className="flex flex-col gap-2 p-4" data-agent-controls>
                <h2 className="m-0 text-[14px] font-bold">مدیریت پرونده</h2>
                <p className="m-0 text-[12.5px] text-fg-muted">
                  مسئول: {t.assignee ? <Ltr>{t.assignee}</Ltr> : 'بدون مسئول'}
                  {t.firstRespondedAt
                    ? ` · اولین پاسخ ${dt(t.firstRespondedAt)}`
                    : ' · هنوز پاسخی ثبت نشده'}
                </p>
                <div className="flex flex-wrap gap-2">
                  {t.mine ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={busy}
                      onPress={() => void update({ assign: 'NONE' })}
                      data-agent-release
                    >
                      رها کردن پرونده
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={busy}
                      onPress={() => void update({ assign: 'ME' })}
                      data-agent-claim
                    >
                      برداشتن پرونده
                    </Button>
                  )}
                  {t.status === 'CLOSED' || t.status === 'RESOLVED' ? (
                    <Button
                      size="sm"
                      variant="secondary"
                      loading={busy}
                      onPress={() => void update({ status: 'OPEN' })}
                      data-agent-reopen
                    >
                      بازگشایی
                    </Button>
                  ) : (
                    <Button
                      size="sm"
                      variant="danger"
                      loading={busy}
                      onPress={() => void update({ status: 'CLOSED' })}
                      data-agent-close
                    >
                      بستن پرونده
                    </Button>
                  )}
                </div>
                <label className="flex flex-col gap-1 text-[12.5px] font-semibold text-fg-muted">
                  اولویت
                  <select
                    value={t.priority}
                    disabled={busy}
                    onChange={(e) => void update({ priority: e.target.value })}
                    className="h-9 rounded-md border border-line-strong bg-surface-1 px-2 text-[13px] font-normal text-fg"
                    data-agent-priority
                  >
                    {Object.entries(PRIORITY).map(([k, v]) => (
                      <option key={k} value={k}>
                        {v}
                      </option>
                    ))}
                  </select>
                </label>
              </Card>
              <Card className="p-4">
                <h2 className="m-0 mb-2 text-[14px] font-bold">رویدادها</h2>
                <ul className="m-0 flex list-none flex-col gap-1 p-0 text-[12px] text-fg-muted">
                  {t.events.map((e, i) => (
                    <li key={i}>
                      {EVENT_FA[e.type] ?? e.type} · {ACTOR_FA[e.actor] ?? e.actor} · {dt(e.at)}{' '}
                      <Ltr className="text-[10.5px] text-fg-subtle">{e.type}</Ltr>
                    </li>
                  ))}
                </ul>
              </Card>
            </aside>
          </div>
        )}
      </Remote>
    </AppContainer>
  );
}
